import React, { useEffect, useState } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { Box, Button, Modal, Typography } from '@mui/material';

const FLAG_KEY = 'sharpstack_arbitrage_tutorial_v1';
const MOBILE_BREAKPOINT = 900;

const STEPS = [
  {
    target: '.filter-section',
    title: 'Filters & Default Stake',
    content:
      'Filter by League and Book to narrow which arbs you see. "Default Stake" sets the base wager — the tool calculates the corresponding stake on the other side so both legs return roughly the same profit.',
    placement: 'bottom-start',
    skipBeacon: true,
  },
  {
    target: '.data-grid-container',
    title: 'Reading an Arb',
    content:
      'Each row is one arbitrage opportunity — two opposite-side bets at different sportsbooks priced to guarantee a profit no matter which side wins. Within a row, the two legs are stacked vertically inside each cell: top half is one leg, bottom half is the other. The Profit column shows your locked-in dollar return plus ROI %.',
    placement: 'center',
    skipBeacon: true,
  },
  {
    target: '.data-grid-container',
    title: 'Pin, Track, Hide & BET',
    content:
      'Each row has columns to act on the arb. Pin sticks an opportunity at the top of the list as new ones come in. Track logs both legs to your Bet Tracker once placed. Hide removes arbs you don\'t want to see. The BET column has two buttons (one per leg) that deep-link into each sportsbook with the line pre-filled — place both to lock in the profit.',
    placement: 'center',
    skipBeacon: true,
  },
  {
    target: '[data-tutorial="arb-settings-gear"]',
    title: 'Settings',
    content:
      'The gear has display settings: "Hide Tracked Arbs" removes arbs you\'ve already tracked from the list, and "Show Hidden Arbs" brings back arbs you hid so you can unhide them.',
    placement: 'bottom-end',
    skipBeacon: true,
  },
];

const CustomTooltip = ({
  index, size, step, backProps, primaryProps, skipProps, tooltipProps, isLastStep,
}) => (
  <div {...tooltipProps} style={{ backgroundColor: '#151a28', border: '1px solid #262d40', borderRadius: 12, padding: '20px 22px 16px', width: 340, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', fontFamily: 'inherit' }}>
    <div style={{ display: 'inline-block', background: '#4caf50', color: '#000', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 100, marginBottom: 10, letterSpacing: 0.5 }}>
      STEP {index + 1} OF {size}
    </div>
    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: '0 0 6px 0' }}>{step.title}</h3>
    <p style={{ fontSize: 13, color: '#8892a8', lineHeight: 1.55, margin: '0 0 16px 0' }}>{step.content}</p>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <button {...skipProps} style={{ background: 'none', border: 'none', color: '#666', fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>Skip tour</button>
      <div style={{ display: 'flex', gap: 6 }}>
        {index > 0 && (
          <button {...backProps} style={{ background: 'none', border: '1px solid #262d40', color: '#8892a8', padding: '7px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Back</button>
        )}
        <button {...primaryProps} style={{ background: '#4caf50', color: '#000', border: 'none', padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          {isLastStep ? 'Got it ✓' : 'Next →'}
        </button>
      </div>
    </div>
  </div>
);

const joyrideStyles = { options: { zIndex: 10000, overlayColor: 'rgba(0, 0, 0, 0.72)', arrowColor: '#151a28' } };

export default function ArbitrageTutorial() {
  const [showWelcome, setShowWelcome] = useState(false);
  const [runTour, setRunTour] = useState(false);

  useEffect(() => {
    if (window.innerWidth < MOBILE_BREAKPOINT) return;
    if (localStorage.getItem(FLAG_KEY)) return;
    const t = setTimeout(() => setShowWelcome(true), 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handleReplay = (e) => {
      if (e.detail?.flag !== FLAG_KEY) return;
      setRunTour(false);
      setShowWelcome(true);
    };
    window.addEventListener('sharpstack:replay-tutorial', handleReplay);
    return () => window.removeEventListener('sharpstack:replay-tutorial', handleReplay);
  }, []);

  const startTour = () => { setShowWelcome(false); setTimeout(() => setRunTour(true), 200); };
  const skipTour = () => { setShowWelcome(false); localStorage.setItem(FLAG_KEY, 'true'); };
  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      localStorage.setItem(FLAG_KEY, 'true');
      setRunTour(false);
    }
  };

  return (
    <>
      <Modal open={showWelcome} onClose={() => {}} disableEscapeKeyDown>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: '#141820', border: '1px solid #252a38', borderRadius: '16px', p: '44px 40px 36px', width: 440, maxWidth: '90vw', textAlign: 'center', outline: 'none', boxShadow: '0 30px 80px rgba(0,0,0,0.6)' }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '14px', background: 'linear-gradient(135deg, #4caf50, #2e7d32)', margin: '0 auto 22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, color: '#000', fontWeight: 800 }}>S</Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#fff', mb: 1 }}>Arbitrage</Typography>
          <Typography sx={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.6, mb: 4, px: 1 }}>
            Place opposite-side bets at different sportsbooks to lock in near-guaranteed profit. Let's show you how.
          </Typography>
          <Button onClick={startTour} sx={{ backgroundColor: '#4caf50', color: '#000', fontWeight: 700, px: 4, py: 1.25, fontSize: 14, textTransform: 'none', borderRadius: 2, '&:hover': { backgroundColor: '#66bb6a' } }}>Show Me</Button>
          <Box sx={{ mt: 1.5 }}>
            <Button onClick={skipTour} sx={{ color: '#4a5068', fontSize: 13, textTransform: 'none', '&:hover': { color: '#94a3b8', background: 'none' } }}>I've got it</Button>
          </Box>
        </Box>
      </Modal>
      <Joyride steps={STEPS} run={runTour} onEvent={handleJoyrideCallback} continuous disableOverlayClose tooltipComponent={CustomTooltip} styles={joyrideStyles} />
    </>
  );
}